import { Link } from 'react-router-dom';
import { richTooltips } from '@dha-nexus/shared';
import './rich-tooltips.css';

const { RICH_TOOLTIP_VARIANTS } = richTooltips;

const WIDTH_LIMITS_BY_VARIANT = {
  [RICH_TOOLTIP_VARIANTS.STANDARD]: { min: 420, ratio: 0.3, max: 560 },
  [RICH_TOOLTIP_VARIANTS.COMPACT]: { min: 320, ratio: 0.22, max: 380 },
};

const HEIGHT_BY_VARIANT = {
  [RICH_TOOLTIP_VARIANTS.STANDARD]: 544,
  [RICH_TOOLTIP_VARIANTS.COMPACT]: 360,
};

function formatTimestamp(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildTestIds(prefix) {
  return {
    root: prefix === 'rich-tooltip' ? 'rich-tooltip-card' : prefix,
    title: `${prefix}-title`,
    summary: `${prefix}-summary`,
    drivers: `${prefix}-drivers`,
    formula: `${prefix}-formula`,
    trust: `${prefix}-trust`,
    warning: `${prefix}-warning`,
    drill: `${prefix}-drill-link`,
  };
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.max(min, Math.min(value, max));
}

function resolvePlacement(anchorRect, variant) {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const widthLimits = WIDTH_LIMITS_BY_VARIANT[variant] || WIDTH_LIMITS_BY_VARIANT.standard;
  const maxWidth = clamp(
    Math.round(viewportWidth * widthLimits.ratio),
    widthLimits.min,
    widthLimits.max,
  );
  const maxHeight = HEIGHT_BY_VARIANT[variant] || HEIGHT_BY_VARIANT.standard;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
  const horizontalGap = 14;
  const verticalGap = 12;
  const stacked = viewportWidth <= 768;
  const availableHeight = Math.max(220, Math.min(maxHeight, viewportHeight - 32));
  const minReadableWidth = variant === RICH_TOOLTIP_VARIANTS.COMPACT ? 260 : 300;
  const copilotDrawer =
    typeof document !== 'undefined' ? document.querySelector('.ai-copilot-drawer.open') : null;
  const copilotRect = copilotDrawer?.getBoundingClientRect?.();
  const rightBoundary =
    copilotRect && copilotRect.left > 0 && copilotRect.left < viewportWidth
      ? Math.floor(copilotRect.left) - 12
      : viewportWidth - 12;
  const availableWidth = Math.max(240, rightBoundary - 12);
  const tooltipWidth = Math.min(maxWidth, availableWidth);

  if (stacked) {
    const left = clamp(anchorRect.left - 24, 12, rightBoundary - tooltipWidth);
    const top = clamp(
      anchorRect.top + anchorRect.height + verticalGap,
      12,
      viewportHeight - availableHeight - 12,
    );
    return { placement: 'below', left, top, width: tooltipWidth, maxHeight: availableHeight };
  }

  const rightSpace = rightBoundary - (anchorRect.left + anchorRect.width + horizontalGap);
  const leftSpace = anchorRect.left - 12 - horizontalGap;
  const canPlaceRight = rightSpace >= tooltipWidth;
  const canPlaceLeft = leftSpace >= tooltipWidth;
  const placement =
    canPlaceRight || (!canPlaceLeft && rightSpace >= Math.min(minReadableWidth, leftSpace))
      ? 'right'
      : 'left';
  const sideSpace = placement === 'right' ? rightSpace : leftSpace;
  const resolvedWidth = Math.max(
    Math.min(tooltipWidth, sideSpace),
    Math.min(sideSpace, minReadableWidth),
  );
  const left =
    placement === 'right'
      ? anchorRect.left + anchorRect.width + horizontalGap
      : clamp(anchorRect.left - resolvedWidth - horizontalGap, 12, rightBoundary - resolvedWidth);
  const top = clamp(
    anchorRect.top + anchorRect.height / 2 - availableHeight / 2,
    12,
    viewportHeight - availableHeight - 12,
  );

  return { placement, left, top, width: resolvedWidth, maxHeight: availableHeight };
}

function toneClassForHeadline(payload) {
  return payload?.headlineValue?.tone ? `tone-${payload.headlineValue.tone}` : 'tone-slate';
}

// Whether a trust block carries anything worth showing. Header tooltips (and
// market-research cells) may have no trust at all — in that case we omit the
// section entirely rather than render a grid of "Unknown" placeholders.
function hasMeaningfulTrust(trust) {
  if (!trust) return false;
  return Boolean(
    trust.sourceSystems?.length ||
    trust.lastUpdatedAt ||
    trust.confidence != null ||
    (trust.freshness && trust.freshness !== 'unknown'),
  );
}

export function RichTooltipCard({
  payload,
  open,
  pinned = false,
  anchorRect,
  testIdPrefix = 'rich-tooltip',
  linkRef,
  tooltipRef,
  onMouseEnter,
  onMouseLeave,
  onRequestClose,
  onDrill,
  dataSurface,
}) {
  if (!open || !payload || !anchorRect) return null;

  const ids = buildTestIds(testIdPrefix);
  const placement = resolvePlacement(anchorRect, payload.variant);
  const reducedMotion = prefersReducedMotion();
  const warnings = payload.trust?.warnings || [];
  const standard = payload.variant !== RICH_TOOLTIP_VARIANTS.COMPACT;

  return (
    <aside
      ref={tooltipRef}
      className={`rich-tooltip-card rich-tooltip-${payload.variant} ${pinned ? 'is-pinned' : ''} ${toneClassForHeadline(payload)} ${reducedMotion ? 'reduced-motion' : ''}`}
      data-testid={ids.root}
      data-placement={placement.placement}
      data-heatmap-surface={dataSurface}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      role={pinned && standard ? 'dialog' : 'tooltip'}
      aria-labelledby={ids.title}
      tabIndex={0}
      style={{
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        width: `${placement.width}px`,
        maxHeight: `${placement.maxHeight}px`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <header className="rich-tooltip-header">
        <div className="rich-tooltip-eyebrow">
          <span>{payload.entityLabel}</span>
          {payload.sliceLabel ? <span>{payload.sliceLabel}</span> : null}
        </div>
        <div className="rich-tooltip-headline">
          <h3 id={ids.title} className="rich-tooltip-title" data-testid={ids.title}>
            {payload.entityLabel}
            {payload.sliceLabel ? ` · ${payload.sliceLabel}` : ''}
          </h3>
          <div className="rich-tooltip-value">
            <strong>{payload.headlineValue.primary}</strong>
            {payload.headlineValue.secondary ? (
              <span>{payload.headlineValue.secondary}</span>
            ) : null}
          </div>
        </div>
      </header>

      {payload.summary ? (
        <section className="rich-tooltip-section">
          <div className="rich-tooltip-label">Summary</div>
          <p id={ids.summary} data-testid={ids.summary} className="rich-tooltip-summary">
            {payload.summary}
          </p>
        </section>
      ) : null}

      {payload.drivers?.length ? (
        <section className="rich-tooltip-section">
          <div className="rich-tooltip-label">{payload.driversLabel || 'Supporting drivers'}</div>
          <ul className="rich-tooltip-drivers" data-testid={ids.drivers}>
            {payload.drivers.map((driver) => (
              <li key={`${driver.label}-${driver.valueLabel}`}>
                <div className="rich-tooltip-driver-head">
                  <strong>{driver.label}</strong>
                  <span>
                    {driver.weightPct != null ? `${driver.weightPct}% · ` : ''}
                    {driver.valueLabel}
                  </span>
                </div>
                {driver.detail ? <p>{driver.detail}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {standard && payload.formula ? (
        <section className="rich-tooltip-section" data-testid={ids.formula}>
          <div className="rich-tooltip-label">{payload.formula.label || 'Scoring rule'}</div>
          <p className="rich-tooltip-summary">{payload.formula.ruleLabel}</p>
          {payload.formula.weightedInputs?.length ? (
            <ul className="rich-tooltip-formula-list">
              {payload.formula.weightedInputs.map((input) => (
                <li key={`${input.label}-${input.valueLabel}`}>
                  <span>{input.label}</span>
                  <span>
                    {input.weightPct}% · {input.valueLabel}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {hasMeaningfulTrust(payload.trust) ? (
        <section className="rich-tooltip-section" data-testid={ids.trust}>
          <div className="rich-tooltip-label">Data trust</div>
          <div className="rich-tooltip-trust-grid">
            <div>
              <span className="rich-tooltip-trust-key">Sources</span>
              <span>{payload.trust?.sourceSystems?.join(', ') || 'Unknown'}</span>
            </div>
            <div>
              <span className="rich-tooltip-trust-key">Last updated</span>
              <span>{formatTimestamp(payload.trust?.lastUpdatedAt)}</span>
            </div>
            <div>
              <span className="rich-tooltip-trust-key">Freshness</span>
              <span>{payload.trust?.freshness || 'Unknown'}</span>
            </div>
            <div>
              <span className="rich-tooltip-trust-key">Confidence</span>
              <span>
                {payload.trust?.confidence == null
                  ? 'Unknown'
                  : payload.trust.confidence.toFixed?.(2) || payload.trust.confidence}
              </span>
            </div>
          </div>
        </section>
      ) : null}

      {warnings.length ? (
        <section className="rich-tooltip-warning" data-testid={ids.warning}>
          {warnings.map((warning, index) => (
            <p key={`${warning}-${index}`}>{warning}</p>
          ))}
        </section>
      ) : null}

      {standard && pinned && payload.drillTarget?.kind === 'route' && payload.drillTarget.href ? (
        <Link
          ref={linkRef}
          to={payload.drillTarget.href}
          className="rich-tooltip-drill-link"
          data-testid={ids.drill}
          onClick={() => onRequestClose?.({ restoreFocus: false })}
        >
          {payload.drillTarget.label}
        </Link>
      ) : null}

      {standard && pinned && payload.drillTarget?.kind === 'drawer' ? (
        <button
          ref={linkRef}
          type="button"
          className="rich-tooltip-drill-link rich-tooltip-drill-button"
          data-testid={ids.drill}
          onClick={() => {
            onDrill?.(payload.drillTarget);
            onRequestClose?.({ restoreFocus: false });
          }}
        >
          {payload.drillTarget.label}
        </button>
      ) : null}
    </aside>
  );
}
